import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { hotjar } from 'react-hotjar';
import posthog from 'posthog-js';
import { ENV } from '@/components/Common/Constants';
import { auth, authCheck } from './asyncActions/auth';

/**
 * Side-effect home for analytics user identification. Kept out of the reducers
 * (which must stay pure) and triggered off the auth thunks instead.
 */
export const analyticsListener = createListenerMiddleware();

// Last identity we sent. `authCheck` re-validates the session on every app
// load, so without this we'd re-identify the same user on every visit.
let lastIdentifiedId: string | null = null;

analyticsListener.startListening({
  matcher: isAnyOf(auth.fulfilled, authCheck.fulfilled),
  effect: (action) => {
    if (ENV !== 'production') return;

    const data = (action.payload as { data?: { email?: string; user_id?: number } })?.data;
    const email = data?.email;
    if (!email) return;

    // Both regions share one PostHog project + one Hotjar site, but each region
    // has its own user DB — so user_id 4521 in the US is a different person from
    // 4521 in the EU. Suffix the region to keep them from colliding/merging.
    const region = process.env.NEXT_PUBLIC_LOCATION === 'EUROPE' ? 'eu' : 'usa';
    const distinctId = `${data?.user_id ?? email}--${region}`;
    if (distinctId === lastIdentifiedId) return;
    lastIdentifiedId = distinctId;

    if (posthog.__loaded) {
      posthog.identify(distinctId, { email, region });
    }
    if (hotjar.initialized()) {
      hotjar.identify(distinctId, { email, region });
    }
  },
});
